import { Component, Prop } from "vue-property-decorator";
import Vue from "vue";
import { GroupFullSchema } from "@/modules/InfoGroup/models/GroupFullSchema";

export type HtmlSchemaField = {
  key: string;
  title: string;
  htmlContent: string;
};

type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

function listControls(root: HTMLElement): Control[] {
  return [...root.querySelectorAll<Control>("input, select, textarea")];
}

/** Primer grupo por `name`; si no hay name, solo el primer control. */
function primaryGroup(nodes: Control[]): Control[] {
  if (!nodes.length) {
    return [];
  }
  const nm = nodes[0].name;
  return nm ? nodes.filter(n => n.name === nm) : [nodes[0]];
}

function readGroup(group: Control[]): unknown {
  if (!group.length) {
    return undefined;
  }
  const a = group[0];

  if (a instanceof HTMLSelectElement) {
    if (a.multiple) {
      return Array.from(a.selectedOptions).map(o => o.value);
    }
    return a.value;
  }

  if (a instanceof HTMLTextAreaElement) {
    return a.value;
  }

  const inp = a as HTMLInputElement;
  const t = (inp.type || "text").toLowerCase();

  if (t === "range") {
    const n = inp.valueAsNumber;
    return Number.isNaN(n) ? inp.value : n;
  }

  if (t === "number") {
    if (inp.value === "") {
      return undefined;
    }
    const n = inp.valueAsNumber;
    return Number.isNaN(n) ? inp.value : n;
  }

  if (t === "radio") {
    const x = group.find(
      e => e instanceof HTMLInputElement && e.type === "radio" && e.checked
    );
    return x?.value;
  }
  if (t === "checkbox") {
    const on = group.filter(
      e => e instanceof HTMLInputElement && e.type === "checkbox" && e.checked
    );
    return on.length <= 1 ? on[0]?.value : on.map(c => c.value);
  }

  return inp.value;
}

function clampOptional(n: number, minAttr: string, maxAttr: string): number {
  let x = n;
  const min = parseFloat(minAttr);
  const max = parseFloat(maxAttr);
  if (!Number.isNaN(min)) {
    x = Math.max(min, x);
  }
  if (!Number.isNaN(max)) {
    x = Math.min(max, x);
  }
  return x;
}

function writeGroup(group: Control[], raw: unknown) {
  if (!group.length) {
    return;
  }
  const str = raw !== undefined && raw !== null ? String(raw) : "";
  const a = group[0];

  if (a instanceof HTMLSelectElement) {
    if (a.multiple && Array.isArray(raw)) {
      const chosen = new Set(raw.map(String));
      Array.from(a.options).forEach(opt => {
        opt.selected = chosen.has(opt.value);
      });
    } else {
      a.value = str;
    }
    return;
  }

  if (a instanceof HTMLTextAreaElement) {
    a.value = str;
    return;
  }

  const inp = a as HTMLInputElement;
  const t = (inp.type || "text").toLowerCase();

  console.log("t ->", t, raw);
  if (t === "range") {
    const num =
      typeof raw === "number"
        ? raw
        : raw !== undefined && raw !== null && raw !== ""
        ? parseFloat(String(raw))
        : NaN;
    console.log("num ->", num);
    if (Number.isNaN(num)) {
      inp.value = inp.defaultValue || String(inp.min || "0");
      return;
    }
    inp.value = String(clampOptional(num, inp.min, inp.max));
    inp.dispatchEvent(new Event("input", { bubbles: true }));
    return;
  }

  if (t === "number") {
    if (raw === undefined || raw === null || raw === "") {
      inp.value = "";
      return;
    }
    const num = typeof raw === "number" ? raw : parseFloat(String(raw));
    inp.value = Number.isNaN(num) ? str : String(num);
    return;
  }

  if (t === "radio") {
    group.forEach(e => {
      const r = e as HTMLInputElement;
      if (r.type === "radio") {
        r.checked = str !== "" && str === r.value;
      }
    });
    return;
  }
  if (t === "checkbox") {
    const set = new Set(
      Array.isArray(raw) ? raw.map(String) : raw ? [str] : []
    );
    group.forEach(e => {
      const c = e as HTMLInputElement;
      if (c.type === "checkbox") {
        c.checked = set.has(c.value);
      }
    });
    return;
  }

  inp.value = str;
}

@Component({
  name: "HtmlRenderFieldsForm",
  model: { prop: "model", event: "update:model" }
})
export default class HtmlRenderFieldsForm extends Vue {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  @Prop({ default: () => ({}) }) model!: Record<string, any>;
  @Prop({ required: true }) schema!: GroupFullSchema;
  @Prop({ default: false }) disabled!: boolean;

  get htmlFields(): HtmlSchemaField[] {
    const list: HtmlSchemaField[] = [];
    const add = (key: string, field: Record<string, unknown>) => {
      if (
        !field ||
        (field["x-display"] !== "html" && field.htmlContent === undefined)
      ) {
        return;
      }
      list.push({
        key,
        title: (field.title as string) || "HTML Field",
        htmlContent:
          (field.htmlContent as string) ||
          "<p style='color:#999'>HTML content not configured</p>"
      });
    };
    if (this.schema.properties) {
      Object.entries(this.schema.properties).forEach(([k, f]) =>
        add(k, (f as unknown) as Record<string, unknown>)
      );
    }
    this.schema.allOf?.forEach(
      (item: { properties?: Record<string, unknown> }) => {
        if (!item.properties) {
          return;
        }
        Object.entries(item.properties).forEach(([k, f]) =>
          add(k, (f as unknown) as Record<string, unknown>)
        );
      }
    );
    return list;
  }

  mounted() {
    console.log("this.model ->", this.model);
    console.log("this.schema ->", this.schema);
    //this.refreshFromModel();
    this.disabled = Object.keys(this.model).length > 0;

    this.paintDomFromModel();
    this.runScripts();
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  patch(patch: Record<string, any>) {
    const next = { ...this.model, ...patch };
    this.$emit("update:model", next);
    this.$emit("input", next);
  }

  onInteract(key: string) {
    if (this.disabled || this.schema?.readOnly) {
      return;
    }
    const el = this.slotRoot(key);
    if (!el) {
      return;
    }
    this.patch({ [key]: readGroup(primaryGroup(listControls(el))) });
  }

  executeEmbeddedScriptsInContainer(container: HTMLElement): void {
    for (const oldEl of [...container.querySelectorAll("script")]) {
      const s = document.createElement("script");
      for (const attr of [...oldEl.attributes]) {
        s.setAttribute(attr.name, attr.value);
      }
      s.textContent = oldEl.textContent;
      oldEl.parentNode?.replaceChild(s, oldEl);
    }
  }

  private slotRoot(key: string): HTMLElement | null {
    return (
      (this.$el as HTMLElement)?.querySelector?.(
        `[data-html-field="${key}"]`
      ) ?? null
    );
  }

  private paintDomFromModel() {
    const ro = this.disabled || !!this.schema?.readOnly;
    for (const hf of this.htmlFields) {
      const el = this.slotRoot(hf.key);
      if (!el) {
        continue;
      }
      console.log("this.model ->", this.model);
      console.log("hf ->", hf);

      writeGroup(primaryGroup(listControls(el)), this.model?.[hf.key]);
      listControls(el).forEach(n => {
        (n as HTMLInputElement).disabled = ro;
      });
    }
  }

  private runScripts() {
    console.log("runScripts");
    for (const hf of this.htmlFields) {
      const el = this.slotRoot(hf.key);
      if (el) {
        this.executeEmbeddedScriptsInContainer(el);
      }
    }
  }
}
